/**
 * Generic SSE hook for consuming POST-based SSE streams.
 * Used for chat, logs, etc.
 */

import { useState, useCallback, useRef } from "react";

interface SSEEvent {
  event: string;
  data: string;
}

interface UseSSEOptions<T> {
  onEvent?: (event: string, data: T) => void;
  onError?: (error: Error) => void;
  onComplete?: () => void;
}

interface UseSSEReturn {
  isStreaming: boolean;
  error: string | null;
  start: (url: string, body?: unknown) => Promise<void>;
  stop: () => void;
}

/**
 * Parses SSE stream from fetch response.
 */
async function* parseSSE(
  response: Response,
): AsyncGenerator<SSEEvent, void, unknown> {
  const reader = response.body?.getReader();
  if (!reader) throw new Error("No response body");

  const decoder = new TextDecoder();
  let buffer = "";

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split("\n");

    // Keep incomplete line in buffer
    buffer = lines.pop() ?? "";

    let currentEvent = "message";
    let currentData = "";

    for (const line of lines) {
      if (line.startsWith("event:")) {
        currentEvent = line.slice(6).trim();
      } else if (line.startsWith("data:")) {
        currentData = line.slice(5).trim();
      } else if (line === "") {
        // Empty line = event boundary
        if (currentData) {
          yield { event: currentEvent, data: currentData };
          currentEvent = "message";
          currentData = "";
        }
      }
    }
  }
}

export function useSSE<T = unknown>(options: UseSSEOptions<T> = {}): UseSSEReturn {
  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const stop = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    setIsStreaming(false);
  }, []);

  const start = useCallback(async (url: string, body?: unknown) => {
    // Abort any existing stream
    stop();

    setError(null);
    setIsStreaming(true);

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const res = await fetch(url, {
        method: body ? "POST" : "GET",
        headers: body ? { "Content-Type": "application/json" } : {},
        body: body ? JSON.stringify(body) : undefined,
        signal: controller.signal,
      });

      if (!res.ok) {
        throw new Error(`HTTP ${res.status}: ${res.statusText}`);
      }

      for await (const { event, data } of parseSSE(res)) {
        if (controller.signal.aborted) break;

        try {
          const parsed = JSON.parse(data) as T;
          options.onEvent?.(event, parsed);
        } catch {
          // Non-JSON data
          options.onEvent?.(event, data as T);
        }
      }

      options.onComplete?.();
    } catch (err) {
      if ((err as Error).name !== "AbortError") {
        const message = err instanceof Error ? err.message : "Stream error";
        setError(message);
        options.onError?.(err instanceof Error ? err : new Error(message));
      }
    } finally {
      setIsStreaming(false);
      abortRef.current = null;
    }
  }, [options, stop]);

  return { isStreaming, error, start, stop };
}
